import { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { ReviewAssembly, ReviewAssemblyFailure } from 'modules/match-video/ui/hooks';
import React from 'react';
import UDButton from 'ud-ui/components/button';
import UDProgressBar from 'ud-ui/components/progress-bar';
import UDText from 'ud-ui/components/text';

const FAILURE_MESSAGES: Record<ReviewAssemblyFailure['code'], MessageDescriptor> = {
  'unsupported-browser': msg`Браузер не умеет собирать видео. Откройте админку в свежем Chrome, Edge или Яндекс Браузере.`,
  'unreadable-clip': msg`Не удалось прочитать клип. Проверьте, что это видеофайл MP4 или MOV.`,
  'template-render': msg`Не удалось нарисовать заставку или плашку. Откройте шаблон в медиа-менеджере и проверьте картинки в нём.`,
  unknown: msg`Сборка прервалась. Попробуйте ещё раз.`,
};

const formatMegabytes = (bytes: number) => (bytes / 1024 / 1024).toFixed(1);

interface BuildProgressProps {
  assembly: ReviewAssembly;
  /** Why «Собрать» is locked; `null` — it is not. */
  blockedReason: string | null;
  onAssemble: () => Promise<void>;
  fileName: string;
}

/**
 * «Собрать» → a bar with «Отменить» → «Скачать». The bar and the status text sit in one slot of a fixed height, so
 * the screen does not jump between the states. Downloading forgets the file at once — it is not kept anywhere.
 */
const BuildProgress = ({ assembly, blockedReason, onAssemble, fileName }: BuildProgressProps) => {
  const { t } = useLingui();
  const isAssembling = assembly.status === 'assembling';
  const percent = Math.round(assembly.progress * 100);
  const sizeMb = assembly.resultSize === null ? null : formatMegabytes(assembly.resultSize);
  const failure = assembly.failure;
  const clipName = failure && 'clipName' in failure ? failure.clipName : undefined;

  return (
    <div className="flex flex-col gap-3" data-testid="match-video-build">
      <div className="flex flex-wrap items-center gap-3">
        {assembly.status === 'ready' ? (
          <UDButton variant="primary" type="button" onClick={() => assembly.download(fileName)}>
            <Trans>Скачать</Trans>
          </UDButton>
        ) : (
          <UDButton variant="primary" type="button" disabled={blockedReason !== null || isAssembling} onClick={onAssemble}>
            <Trans>Собрать</Trans>
          </UDButton>
        )}
        {isAssembling && (
          <UDButton variant="secondary" type="button" onClick={assembly.cancel}>
            <Trans>Отменить</Trans>
          </UDButton>
        )}
        {assembly.status === 'ready' && (
          <UDButton variant="secondary" type="button" onClick={assembly.reset}>
            <Trans>Собрать заново</Trans>
          </UDButton>
        )}
      </div>

      <div className="flex min-h-10 flex-col justify-center gap-2">
        {isAssembling && (
          <>
            <UDText type="footnote" role="status" className="text-inkSecondary">
              <Trans>Собираем… {percent}%. Не закрывайте вкладку — видео собирается на этом компьютере.</Trans>
            </UDText>
            <UDProgressBar value={assembly.progress} label={t`Сборка видеообзора`} className="max-w-xl" />
          </>
        )}
        {assembly.status === 'ready' && sizeMb !== null && (
          <UDText type="footnote" role="status" className="text-inkSecondary">
            <Trans>Готово: {sizeMb} МБ. После скачивания файл не хранится — соберите заново, если понадобится.</Trans>
          </UDText>
        )}
        {failure && (
          <UDText type="footnote" role="alert" className="text-CaseErrorText">
            {t(FAILURE_MESSAGES[failure.code])}
            {clipName ? ` ${clipName}` : ''}
          </UDText>
        )}
        {assembly.status === 'idle' && blockedReason && (
          <UDText type="footnote" className="text-inkSecondary" data-testid="match-video-blocked">
            {blockedReason}
          </UDText>
        )}
      </div>
    </div>
  );
};

export default BuildProgress;
